import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Lock11Blue } from "@similarweb/icons/illustrations";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import HooksModalV5Key from "app/constants/HooksModalV5Key";
import { useStorage } from "app/custom-hooks/storage/useStorage";
import ModalContent from "app/pages/sales-intelligence/common-components/ContactUsModal/modalContent/ModalContent";
import { GainAccessButton } from "app/pages/sales-intelligence/common-components/GainAccessButton";
import useRequestFeatureHookWithLoading from "app/pages/sales-intelligence/hooks/useRequestFeatureHookWithLoading";

import { appsSearchSessionStorage } from "../../../services/storage";
import appsSearchI18nKeys from "../../../utils/i18n";

const AppsLockedOverlay = () => {
    const { translate } = useTranslate();

    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const [isFeatureRequestSent, setIsFeatureRequestSent] = useStorage(appsSearchSessionStorage.appsFeatureRequestSent);

    const { isRequestInProgress, requestFeature } = useRequestFeatureHookWithLoading();

    const handleClick = async (): Promise<void> => {
        if (isFeatureRequestSent) {
            setIsSuccessModalOpen(true);

            return;
        }

        if (isRequestInProgress) {
            return;
        }

        try {
            await requestFeature(HooksModalV5Key.AppLeadGenerator);
            setIsSuccessModalOpen(true);
            setIsFeatureRequestSent(true);
        } catch (error) {
            Sentry.errorTracker({
                message: "Error sending a feature request",
                e: error,
                level: "error",
                params: {
                    extra: {
                        featureKey: HooksModalV5Key.AppLeadGenerator,
                    },
                },
            });
        }
    };

    return (
        <>
            <Stack
                data-automation="apps-locked-overlay"
                alignItems="center"
                justifyContent="center"
                rowGap="24px"
            >
                <Lock11Blue size={219} />

                <Stack
                    gap="8px"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Text
                        fontVariant="subtitle1"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate(appsSearchI18nKeys.lockedStateTitle)}
                    </Text>

                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate(appsSearchI18nKeys.lockedStateSubtitle)}
                    </Text>
                </Stack>

                <GainAccessButton
                    data-automation="apps-get-access-button"
                    type="trial"
                    onClick={handleClick}
                />
            </Stack>

            <Modal isOpen={isSuccessModalOpen}>
                <ModalContent
                    title={translate("hook_modal.get_feature.success.title")}
                    subTitle={translate("si.modal.get_feature.success.subtitle.v-2")}
                    buttonText={translate("si.modal.get_feature.success.btn.close")}
                    onClick={() => setIsSuccessModalOpen(false)}
                />
            </Modal>
        </>
    );
};

export default AppsLockedOverlay;
